import { useEffect, useRef, useState } from "react";
import { Plus, Search } from "lucide-react";
import { toast } from "sonner";
import { agruparChips, ehDonoOuSocio, favoritosPrimeiro } from "@bookdental/schemas";
import { trpc } from "@/lib/trpc";
import { useAuth } from "@/lib/auth";
import { useFavoritos } from "@/lib/use-favoritos";
import { EstrelaDeFavorito } from "@/components/shared/EstrelaDeFavorito";
import type { ChipService } from "../ServiceChips";
import { bareInputCls } from "./CampoDaConsulta";

export type ServicoDoCatalogo = ChipService & { especialidade?: { label: string } | null };

const ROTULO = { favoritos: "Favoritos", profissional: "Do profissional", todos: "Sugestões" } as const;
const SUGESTOES = 8;
const normalizar = (s: string) => s.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();

/**
 * Busca de serviços da consulta. Campo vazio: sugere favoritos e os serviços do profissional
 * (o atalho que antes eram os chips sempre abertos). Digitando: filtra o catálogo inteiro, que já
 * está carregado, por nome e especialidade — e cria um serviço novo se não achar.
 */
export function BuscaDeServicos({
  servicos,
  selecionados,
  atribuidos,
  onEscolher,
  onCriado,
}: {
  servicos: ServicoDoCatalogo[];
  selecionados: string[];
  atribuidos: string[];
  onEscolher: (id: string) => void;
  onCriado: () => void;
}) {
  const { user } = useAuth();
  const podeCriar = ehDonoOuSocio(user?.activeOrganization?.role) || !!user?.activeOrganization?.permissions?.["settings.manage"];
  const [texto, setTexto] = useState("");
  const [aberto, setAberto] = useState(false);
  const caixa = useRef<HTMLDivElement>(null);
  const utils = trpc.useUtils();
  const favoritos = useFavoritos("procedimento");

  useEffect(() => {
    const fora = (e: MouseEvent) => caixa.current && !caixa.current.contains(e.target as Node) && setAberto(false);
    if (aberto) document.addEventListener("mousedown", fora);
    return () => document.removeEventListener("mousedown", fora);
  }, [aberto]);

  const escolher = (id: string) => {
    onEscolher(id);
    setTexto("");
    setAberto(false);
  };

  const criar = trpc.service.create.useMutation({
    onSuccess: (novo) => {
      utils.service.list.invalidate();
      onCriado();
      escolher(novo.id);
      toast.success("Procedimento criado.");
    },
    onError: (e) => toast.error(e.message || "Erro ao criar procedimento."),
  });

  const livres = servicos.filter((s) => !selecionados.includes(s.id));
  const busca = normalizar(texto.trim());
  const grupos = busca
    ? [{ chave: null, itens: favoritosPrimeiro(livres.filter((s) => normalizar(`${s.name} ${s.especialidade?.label ?? ""}`).includes(busca)), favoritos.ids) }]
    : agruparChips(livres, { selecionados: [], atribuidos, favoritos: favoritos.ids, maxVisivel: SUGESTOES }).grupos.map((g) => ({
        chave: ROTULO[g.chave],
        itens: g.itens.slice(0, SUGESTOES),
      }));
  const primeiro = grupos[0]?.itens[0];
  const exato = servicos.some((s) => normalizar(s.name) === busca);
  const mostrarCriar = podeCriar && texto.trim().length >= 2 && !exato;

  return (
    <div ref={caixa} className="relative">
      <div className="flex items-center gap-2">
        <Search className="h-3.5 w-3.5 shrink-0 text-zinc-400" />
        <input
          className={bareInputCls}
          value={texto}
          onChange={(e) => {
            setTexto(e.target.value);
            setAberto(true);
          }}
          onFocus={() => setAberto(true)}
          onClick={() => setAberto(true)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && primeiro) {
              e.preventDefault();
              escolher(primeiro.id);
            }
          }}
          placeholder="Buscar procedimento..."
        />
      </div>

      {aberto && (
        <div className="absolute left-0 right-0 top-full z-50 mt-2 max-h-[260px] overflow-auto rounded-md border border-zinc-200 bg-white py-1 shadow-xl dark:border-border dark:bg-card">
          {grupos.every((g) => g.itens.length === 0) && !mostrarCriar && (
            <p className="px-3 py-2 text-[12px] text-zinc-500 dark:text-muted-foreground">
              {busca ? "Nenhum procedimento encontrado." : "Digite para buscar no catálogo."}
            </p>
          )}
          {grupos.map((g, i) =>
            g.itens.length === 0 ? null : (
              <div key={g.chave ?? i}>
                {g.chave && (
                  <p className="px-3 pb-0.5 pt-1.5 text-[10px] font-semibold uppercase tracking-wider text-zinc-400 dark:text-muted-foreground/70">{g.chave}</p>
                )}
                {g.itens.map((s) => (
                  <div key={s.id} className="flex items-center pr-2 hover:bg-zinc-50 dark:hover:bg-muted/40">
                    <button type="button" onClick={() => escolher(s.id)} className="flex min-w-0 flex-1 items-center gap-2.5 px-3 py-1.5 text-left">
                      <span className="h-2 w-2 shrink-0 rounded-full bg-zinc-300" style={s.color ? { backgroundColor: s.color } : undefined} />
                      <span className="min-w-0 flex-1 truncate text-sm text-zinc-900 dark:text-foreground">{s.name}</span>
                      <span className="shrink-0 text-[11px] text-zinc-400 dark:text-muted-foreground">
                        {[s.especialidade?.label, s.durationMinutes ? `${s.durationMinutes} min` : null].filter(Boolean).join(" · ")}
                      </span>
                    </button>
                    <EstrelaDeFavorito ativo={favoritos.ids.has(s.id)} onAlternar={() => favoritos.alternar(s.id)} />
                  </div>
                ))}
              </div>
            ),
          )}
          {!busca && livres.length > SUGESTOES && (
            <p className="px-3 pb-1 pt-1.5 text-[11px] text-zinc-400 dark:text-muted-foreground">Digite para ver os {livres.length} procedimentos do catálogo.</p>
          )}
          {mostrarCriar && (
            <button
              type="button"
              disabled={criar.isPending}
              onClick={() => criar.mutate({ name: texto.trim(), priceCents: 0 })}
              className="flex w-full items-center gap-2 border-t border-zinc-100 px-3 py-2 text-left text-brand-700 hover:bg-brand-50 disabled:opacity-60 dark:border-border dark:text-primary dark:hover:bg-primary/10"
            >
              <Plus className="h-3.5 w-3.5" />
              <span className="text-sm">
                Criar procedimento: <span className="font-semibold">"{texto.trim()}"</span>
              </span>
            </button>
          )}
        </div>
      )}
    </div>
  );
}
